import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { TempLogo } from "@/assets/temp-logo";
import Footer from "@/components/Common/Footer";
import VerifyEmail from "../../pages/VerifyEmailContainer";
import { useForgotPassword } from "../../api/auth.queries";
import { Spinner } from "@/components/ui/spinner";

// ✅ Schema
const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { mutate: forgotPasswordMutate, isPending } = useForgotPassword();

  const [showVerifyEmail, setShowVerifyEmail] = useState(false);

  const form = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  function onSubmit(values: ForgotPasswordSchema) {
    forgotPasswordMutate(
      {
        email: values.email,
        app_id: "68958c254d1efc253f7380e0",
      },
      {
        onSuccess: () => {
          setShowVerifyEmail(true);
        },
      },
    );
  }

  if (showVerifyEmail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 pb-8">
        <VerifyEmail forgotPassword />
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between px-4 pb-8">
      <div className="flex w-full max-w-[420px] flex-grow flex-col justify-center gap-8">
        {/* Logo */}
        <div className="text-center">
          <div className="flex justify-center">
            <TempLogo width={68} height={44} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">metawhale</h1>
        </div>

        {/* Back */}
        <p
          className="text-muted flex cursor-pointer items-center gap-1 text-sm leading-normal font-bold tracking-normal"
          onClick={() => navigate({ to: "/login" })}
        >
          <i className="ri-arrow-left-long-line"></i> Back
        </p>

        {/* Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-0">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Email Address <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="email@example.com"
                      variant="filled"
                      className="h-[45px]"
                      autoComplete="email"
                    />
                  </FormControl>
                  <FormMessage className="min-h-[16px] text-xs text-red-600" />
                </FormItem>
              )}
            />

            <div className="mt-10 mb-[12px]">
              <Button
                type="submit"
                className="text-md h-[48px] w-full leading-normal"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex h-full items-center justify-center text-white">
                    <Spinner size="sm" className="stroke-white!" />
                  </div>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </div>
          </form>
        </Form>

        {/* Footer Note */}
        <div className="text-center text-sm font-semibold text-neutral-800">
          If you have any questions, please contact <br />
          <a
            href="mailto:support@media-meter.com"
            className="text-brand-600 hover:text-orange-600"
          >
            support@media-meter.com
          </a>
          . We are happy to help!
        </div>
      </div>
      <Footer />
    </div>
  );
}
